/**
 * Tax rates. Defined on the LOCATION; a menu only uses them.
 *
 * Two things shape every screen here:
 *
 * - Where they come from depends on the location's mode. On a `pos` location
 *   the POS owns them: they are synced from the register and every write
 *   (create / update / delete / assign) answers 400. On a `tablet` location an
 *   admin enters them.
 * - `applyToAllItems` is the only rule there is. A rate carrying it lands on
 *   every item and option that has no selection of its own; a rate without it
 *   (alcohol tax, say) lands only where it is explicitly assigned. An item's
 *   own selection REPLACES the location's rates - it never adds to them.
 */

export enum TaxRateType {
  /** `rate` is a percentage times 10.000: `88750` is 8.875%. */
  percentage = 'percentage',
  /** `rate` is an amount in cents, charged per item: `150` is $1.50. */
  fixed = 'fixed',
}

export enum TaxRateSource {
  /** Entered by an admin. */
  manual = 'manual',
  /** Synced from the POS. Read-only everywhere. */
  pos = 'pos',
}

export enum TaxRateAssignmentType {
  item = 'item',
  option = 'option',
}

/** One item or option a non-`applyToAllItems` rate is explicitly set on. */
export type TaxRateAssignment = {
  type: TaxRateAssignmentType;
  id: number;
  name: string;
  brandId: number;
  brandName: string;
  /** True for the location's own brand, false for a virtual one. */
  isLocation: boolean;
  /**
   * Which menu the assignment belongs to. Multi menu only - the same item
   * copied into a second menu arrives as a second assignment row. Absent
   * until multi menu ships, which is why both are optional.
   */
  menuId?: number;
  menuName?: string;
};

/**
 * A tax rate as a menu item or option carries it: the rate itself, without
 * the location-level bookkeeping (`editable`, `assignments`).
 */
export type TaxRateEntity = {
  id: number;
  name: string;
  /** Scaled per `type` - read it through `taxRateAmountLabel`. */
  rate: number;
  type: TaxRateType;
  /** Price already includes the tax. Always false for us; FoodHub has it. */
  inclusive: boolean;
  applyToAllItems: boolean;
  source: TaxRateSource;
  posReference: string;
};

/** A tax rate as the location's list returns it. */
export type LocationTaxRateEntity = TaxRateEntity & {
  /** False for anything synced from the POS - show it locked. */
  editable: boolean;
  /** Only filled for `applyToAllItems: false` rates; empty otherwise. */
  assignments: TaxRateAssignment[];
};

export type CreateTaxRateDto = {
  name: string;
  rate: number;
  type: TaxRateType;
  /** Optional, defaults to false. */
  inclusive?: boolean;
  /** Optional, defaults to true. */
  applyToAllItems?: boolean;
};

/** Every field optional - send only what changed. */
export type UpdateTaxRateDto = Partial<CreateTaxRateDto>;

/**
 * A rate that does not exist yet: what the location verification form
 * collects. The rates go out with `POST /v1/locations/:id/verify` and are
 * written in the same transaction, so nothing here has an id until the
 * location is verified - `key` is local, only so the list can be keyed and
 * edited.
 */
export type TaxRateDraft = {
  key: string;
  name: string;
  rate: number;
  type: TaxRateType;
  applyToAllItems: boolean;
};

/** Ids of the location's rates. An empty list drops the item's own choice. */
export type SetTaxRatesDto = {
  taxRateIds: number[];
};

export enum TaxRateSuggestionConfidence {
  high = 'high',
  medium = 'medium',
  low = 'low',
}

/**
 * What the AI lookup answers with. It writes nothing: the admin reviews the
 * rate and saves it like any other.
 *
 * Only `high` is trusted enough for the backfill to write on its own, and the
 * measurement behind that threshold is worth respecting on screen too - every
 * wrong answer in the sample was a `medium` or `low` one.
 */
export type TaxRateSuggestion = {
  /** A percentage as a person reads it: `11.3` is 11.3%. */
  ratePercent: number;
  /** The same rate already scaled, ready for the field. */
  rate?: number;
  /** The address the rate was read off. */
  address?: string;
  /** What the rate is made of, e.g. "VA state sales tax 5.3%". */
  components: string[];
  /** Pages the model read. */
  sources: string[];
  confidence: TaxRateSuggestionConfidence;
  note: string;
  /**
   * The backend's own verdict, which is what the backfill writes on. False
   * means show the answer but make someone check it - the threshold behind
   * this flag is the measured one, so trust it over the confidence alone.
   */
  trusted?: boolean;
};
